import { ResourcesValue } from '../../resources/resources';
import { type LifecycleHooks } from '../addLifecycleHooks';
import { type AdminDataProvider } from '../adminDataProvider';
import { type FileHostType } from '../customEndpoints/uploadFile';

export type ReactAdminEditedFile = {
  /**
   * The file name with extension, e.g. 'Invoice-2929-01-06.pdf'
   */
  title: string;
  /**
   * An object URL for the {@link File}, e.g. `'blob:https://example.com/1e67e00e-860d-40a5-89ae-6ab0cbee6273'`
   */
  src: string;
  /**
   * The {@link File} object
   */
  rawFile: File;
};

type ReactAdminUneditedFile = {
  /**
   * the file name with extension, e.g. ‘Invoice-2929-01-06.pdf’
   */
  title: string;
  /**
   * The public URL where the file is hosted
   */
  src: string;
};

export type ReactAdminFile = ReactAdminEditedFile | ReactAdminUneditedFile;

export const fileInputPendingUpload = (
  file: ReactAdminFile | undefined
): file is ReactAdminEditedFile => {
  return !!file && 'rawFile' in file;
};

/**
 * This is typed so that any files that you _know_ aren't `undefined` will never return `undefined` from {@link handleUpload}
 *
 * But if you pass it something that might be `null` or `undefined` (such as `any`), it will return a value of type "might be `undefined`"
 */
type UploadResult<TFile extends ReactAdminFile> = TFile extends null | undefined
  ? ReactAdminUneditedFile | undefined
  : ReactAdminUneditedFile;

export const handleUpload = async <TFile extends ReactAdminFile>(
  file: TFile | undefined,
  hostType: FileHostType,
  dataProvider: AdminDataProvider
): Promise<UploadResult<TFile>> => {
  if (!file) {
    return undefined as UploadResult<TFile>;
  }

  if (!fileInputPendingUpload(file)) {
    return file;
  }

  const newUrl = await dataProvider.uploadFile(file, hostType);
  return {
    src: newUrl,
    title: file.title,
  };
};

/**
 * Lifecycle hooks for the data provider to upload files to the file host
 * @see https://marmelab.com/react-admin/DataProviders.html#sending-files-to-a-third-party-service
 * @see https://marmelab.com/react-admin/withLifecycleCallbacks.html#beforesave
 */
export const fileUploadHooks: LifecycleHooks[] = [
  {
    resource: ResourcesValue.NUDGES,
    beforeSave: async (params, dataProvider) => {
      params.image = await handleUpload(
        params.image,
        'images',
        dataProvider as AdminDataProvider
      );
      return params;
    },
  },
  {
    resource: ResourcesValue.NUDGE_SLIDES,
    beforeSave: async (params, dataProvider) => {
      params.image = await handleUpload(
        params.image,
        'images',
        dataProvider as AdminDataProvider
      );

      return params;
    },
  },
  {
    resource: ResourcesValue.BADGES,
    beforeSave: async (params, dataProvider) => {
      params.image = await handleUpload(
        params.image,
        'images',
        dataProvider as AdminDataProvider
      );
      return params;
    },
  },
  {
    resource: ResourcesValue.JOURNEYS,
    beforeSave: async (params, dataProvider) => {
      const savedBanner = await handleUpload(
        params.bannerImage,
        'images',
        dataProvider as AdminDataProvider
      );
      params.bannerImageUrl = savedBanner?.src;

      return params;
    },
  },
];
